'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react'

import { AD_GLOBAL_STATUS } from '@/defs/ad'
import { useReduxSelector } from '@/store/hooks'
import { selectors } from '@/store/selectors'
import {
  observeMediavineAdhesion,
  removeMediavineAdboxes,
  removeMediavineContainers,
  removeMediavineVariables,
} from '@/utils/adUtil'
import { setAdCookie } from '@/utils/cookieUtil'

export type AdsContextProps = {
  adsDisabled?: boolean
}

const AdsContext = createContext<AdsContextProps | undefined>(undefined)

type AdsProviderProps = {
  children: ReactNode
}

export const AdsProvider = ({ children }: AdsProviderProps) => {
  const userStatus = useReduxSelector((s) => s.user.status)
  const patreonTier = useReduxSelector(selectors.patreonTier)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const searchParamsString = searchParams.toString()
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!isClient) setIsClient(true)
  }, [isClient])

  const adsDisabled = useMemo(() => {
    if (patreonTier) return true
    if (isClient && document.documentElement.dataset.ad === 'off' && userStatus === 'loading') return true
    return false
  }, [isClient, userStatus, patreonTier])

  // Once auth resolves, persist whether this browser should defer ads next time
  useEffect(() => {
    if (!isClient || userStatus === 'loading') return

    setAdCookie(!patreonTier)
  }, [isClient, userStatus, patreonTier])

  // Remove Mediavine ads if user has Patreon tier
  useEffect(() => {
    if (adsDisabled) {
      removeMediavineContainers()
      removeMediavineAdboxes()
      removeMediavineVariables()
      return
    }
    const unsubObserveAdhesion = observeMediavineAdhesion()

    return () => {
      unsubObserveAdhesion()
    }
  }, [adsDisabled])

  // Remove Mediavine ads on route change, which Mediavine does already but sometimes fails
  useEffect(() => {
    removeMediavineContainers()
  }, [pathname, searchParamsString])

  const value = useMemo(() => ({ adsDisabled }), [adsDisabled])

  return <AdsContext.Provider value={value}>{children}</AdsContext.Provider>
}

export const useAds = () => {
  const adsContext = useContext(AdsContext)

  if (!adsContext) throw new Error('useAds must be used within an AdsProvider')

  const { adsDisabled } = adsContext

  return {
    adsDisabled,
    galleryIncontentDisabled: adsDisabled || !AD_GLOBAL_STATUS.GALLERY_INCONTENT,
    leaderboardDisabled: adsDisabled || !AD_GLOBAL_STATUS.LEADERBOARD,
    midBannerDisabled: adsDisabled || !AD_GLOBAL_STATUS.MID_BANNER,
    sidebarDisabled: adsDisabled || !AD_GLOBAL_STATUS.SIDEBAR,
    uvpPlusDisabled: adsDisabled || !AD_GLOBAL_STATUS.UVPPLUS,
  }
}
